/**
 * Sales & Telecalling hierarchy — API hooks.
 *
 * Talks to the mobile BFF (mobile-app/backend), which is the single place the hierarchy
 * rules live. Authentication is a session cookie, the same as the rest of the ERP: the
 * console hands over its existing ERP session once at `/auth/session-exchange`, the BFF
 * verifies it against the ERP's own backend and starts a session of its own.
 *
 * This used to keep a JWT in sessionStorage and re-exchange on 401. That is gone —
 * nothing identifying is held in the tab any more, and a session ended on the server is
 * ended here on the next request rather than whenever a token would have lapsed.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const RAW_BASE = import.meta.env.VITE_MOBILE_API_URL || 'http://localhost:4300';
export const MOBILE_API_BASE = RAW_BASE.replace(/\/$/, '');

const api = axios.create({ baseURL: MOBILE_API_BASE, withCredentials: true });

/**
 * The same client, for pages outside the Command Center.
 *
 * The ERP's own screens now show call recordings, and they need the session exchange and
 * the 401 retry below exactly as these hooks do. Exporting the instance rather than
 * letting those pages build their own is what stops a second client existing that has
 * neither.
 */
export const mobileApi = api;

/**
 * Trades the ERP session cookie for one of this service's own. Called once per tab; the
 * cookie carries it from there.
 */
export const exchangeSession = async () => {
  const { data } = await axios.post(
    `${MOBILE_API_BASE}/auth/session-exchange`,
    {},
    { withCredentials: true }
  );
  return data.data;
};

/** One retry through the exchange when the BFF session has lapsed but the ERP's has not. */
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || original?._retried) return Promise.reject(error);
    original._retried = true;
    try {
      await exchangeSession();
      return await api(original);
    } catch (err) {
      return Promise.reject(err);
    }
  }
);

export const apiMessage = (error) => {
  /* ERR_NETWORK, not the message text — see the note in the app's normaliseError. */
  if (
    error?.code === 'ERR_NETWORK' ||
    error?.code === 'ECONNABORTED' ||
    error?.message === 'Network Error'
  ) {
    return 'Could not connect to the server. Please check your internet connection or try again later.';
  }
  return error?.response?.data?.message || error?.message || 'Something went wrong.';
};

const unwrap = (r) => r.data?.data;

// ─── Session ──────────────────────────────────────────────────────
export function useOrgSession() {
  return useQuery({
    queryKey: ['org-session'],
    /*
     * Exchange first, every time. The ERP session is the authority on who this is.
     *
     * This used to ask `/auth/me` and only exchange when that failed, which trusted
     * whatever cookie this service had left in the browser. A second person signing in
     * to the ERP on the same machine inherited the first person's BFF session: the ERP
     * header read the new name while the queues, the thirty-day figures and the activity
     * feed underneath were all still the previous user's. Nothing 401'd, so nothing ever
     * corrected it.
     *
     * One POST per mount, bounded by staleTime below, and the server only audits it as a
     * login when the person actually changed.
     *
     * `/auth/me` stays as a fallback, but only for when the exchange cannot run at all —
     * the ERP backend down, or the ERP cookie not reaching this service because the two
     * are not same-site in some deployment. Exchanging first and falling back is the
     * difference between degrading to the old behaviour and locking the module out
     * entirely; it must never be the other way round, because preferring an existing
     * cookie is the bug this replaced.
     */
    queryFn: async () => {
      try {
        return await exchangeSession();
      } catch {
        return api.get('/auth/me').then(unwrap);
      }
    },
    retry: false,
    staleTime: 60_000,
    /**
     * Keep trying, quietly, until there is a session.
     *
     * Without this a single failure — the ERP backend restarting, a dropped connection
     * at page load — left the whole module signed out until something forced a remount.
     * Once a session exists this stops; it only ever runs while there is none.
     */
    refetchInterval: (query) => (query.state.data ? false : 30_000),
    refetchOnWindowFocus: true,
  });
}

// ─── Reference data ───────────────────────────────────────────────
export function useHierarchyMeta() {
  return useQuery({
    queryKey: ['org-meta'],
    queryFn: () => axios.get(`${MOBILE_API_BASE}/meta`).then(unwrap),
    staleTime: Infinity,
  });
}

// ─── Team ─────────────────────────────────────────────────────────
export function useTeam(enabled = true) {
  return useQuery({
    queryKey: ['org-team'],
    queryFn: () => api.get('/team').then(unwrap),
    enabled,
  });
}

export function useMember(memberId) {
  return useQuery({
    queryKey: ['org-member', memberId],
    queryFn: () => api.get(`/team/members/${memberId}`).then(unwrap),
    enabled: !!memberId,
  });
}

export function useCandidates(enabled = true) {
  return useQuery({
    queryKey: ['org-candidates'],
    queryFn: () => api.get('/team/candidates').then(unwrap),
    enabled,
  });
}

/**
 * Hands one record to somebody. The same endpoint the app's assign sheet uses, so a
 * record assigned from either side lands in the same place and shows the same way.
 */
/*
 * Every list that can now be wrong when a record changes hands. The record leaves one
 * queue, appears in the team's assignment lists, and changes the module's work counts —
 * invalidating only the queue would leave the other two showing yesterday's picture
 * until something else happened to refetch them.
 *
 * Shared by assigning and unassigning because they disturb exactly the same lists in
 * opposite directions. Kept as one list so the two cannot drift apart: the bug that
 * follows a copy is one side gaining an invalidation the other never does, and it
 * shows up as a row that vanishes on assign but lingers on take-back.
 */
const ASSIGNMENT_LISTS = [
  ['scc-smart-queue'],
  ['scc-module-work'],
  ['scc-team-assignments-list'],
  ['org-team-assignments'],
  ['org-work-summary'],
];

const invalidateAssignments = (qc) => {
  ASSIGNMENT_LISTS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
};

export function useAssignRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, module, recordId, priority, instructions }) =>
      api.post('/assignments', { memberId, module, recordId, priority, instructions }).then(unwrap),
    onSuccess: () => invalidateAssignments(qc),
  });
}

/**
 * Hands a record back to the unassigned pool.
 *
 * The assignment is cancelled, never deleted — who held it and what they did on it is
 * the work history, and a take-back is not a reason to lose it. The server decides
 * whether this person may take it back; the button only offers it.
 */
export function useUnassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, note }) =>
      api.post(`/assignments/${assignmentId}/unassign`, { note }).then(unwrap),
    onSuccess: () => invalidateAssignments(qc),
  });
}

/** The ERP "Handled by" names a member can be mapped to, read from the ERP itself. */
export function useErpOwners(enabled = true) {
  return useQuery({
    queryKey: ['scc-erp-owners'],
    queryFn: () => api.get('/team/erp-owners').then(unwrap),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDelegationOptions(enabled = true) {
  return useQuery({
    queryKey: ['org-delegation-options'],
    queryFn: () => api.get('/team/delegation-options').then(unwrap),
    enabled,
  });
}

export function useGrantHistory(enabled = true) {
  return useQuery({
    queryKey: ['org-grants'],
    queryFn: () => api.get('/team/grants', { params: { limit: 50 } }).then((r) => r.data),
    enabled,
  });
}

// ─── Work & activity ──────────────────────────────────────────────
export function useWorkSummary(enabled = true) {
  return useQuery({
    queryKey: ['org-work-summary'],
    queryFn: () => api.get('/work/summary').then(unwrap),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useTeamAssignments(params, enabled = true) {
  return useQuery({
    queryKey: ['org-team-assignments', params],
    queryFn: () => api.get('/assignments/team', { params }).then((r) => r.data),
    enabled,
  });
}

export function useActivityFeed(params, enabled = true) {
  return useQuery({
    queryKey: ['org-activity', params],
    queryFn: () => api.get('/activity', { params }).then((r) => r.data),
    enabled,
    refetchInterval: 45_000,
  });
}

// ─── Real calling data (same endpoints the mobile app uses) ───────
export function useModuleSummary(enabled = true) {
  return useQuery({
    queryKey: ['scc-module-summary'],
    queryFn: () => api.get('/work/summary').then(unwrap),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useModuleWork(moduleKey, params, enabled = true) {
  return useQuery({
    queryKey: ['scc-module-work', moduleKey, params],
    queryFn: () => api.get(`/work/${moduleKey}`, { params }).then((r) => r.data),
    enabled: enabled && !!moduleKey,
  });
}

/**
 * `bucket` filters the same way the phone's module queue does; `forDialling` asks for a
 * run instead — today's promises then the overdue ones, never anything upcoming.
 */
export function useSmartQueue(moduleKey, enabled = true, params = {}) {
  return useQuery({
    queryKey: ['scc-smart-queue', moduleKey, params],
    /*
     * `expand=contacts` gives one row per scheduled person instead of one per account,
     * ordered by the hour each call is due, so another company's 14:00 sits between this
     * account's 11:00 and 16:00. Asked for here and not on the phone: this table has a
     * column for a person and a time, the handset's card does not.
     */
    queryFn: () =>
      api
        .get(`/queue/${moduleKey}`, { params: { limit: 50, expand: 'contacts', ...params } })
        .then(unwrap),
    enabled: enabled && !!moduleKey,
    refetchInterval: 30_000,
  });
}

/**
 * Everything due today across every module — the run the call console works through.
 *
 * Deliberately not refetched on an interval: the console holds a position in this
 * list, and a queue that reorders itself under a caller mid-run is worse than one
 * that is thirty seconds stale.
 */
export function useDayQueue(enabled = true, params = {}) {
  return useQuery({
    queryKey: ['scc-day-queue', params],
    queryFn: () => api.get('/queue/today', { params: { limit: 60, ...params } }).then(unwrap),
    enabled,
  });
}

export function useFeedbackForm(moduleKey, enabled = true) {
  return useQuery({
    queryKey: ['scc-feedback-form', moduleKey],
    queryFn: () => api.get(`/calls/form/${moduleKey}`).then(unwrap),
    enabled: enabled && !!moduleKey,
    staleTime: Infinity,
  });
}

export function useRecord(moduleKey, recordId, enabled = true) {
  return useQuery({
    queryKey: ['scc-record', moduleKey, recordId],
    queryFn: () => api.get(`/work/${moduleKey}/records/${recordId}`).then(unwrap),
    enabled: enabled && !!moduleKey && !!recordId,
  });
}

/** Every remark ever recorded on a record — the ERP's trail plus this app's calls. */
export function useRecordNotes(moduleKey, recordId, enabled = true) {
  return useQuery({
    queryKey: ['scc-record-notes', moduleKey, recordId],
    queryFn: () => api.get(`/work/${moduleKey}/records/${recordId}/notes`).then(unwrap),
    enabled: enabled && !!moduleKey && !!recordId,
  });
}

/**
 * The call this person currently has open — started here, or on their phone.
 *
 * A call belongs to the caller, not to the device that dialled it. Somebody who
 * presses call on the mobile app and then sits down at their desk is on the same
 * call, so the console follows it: it polls for whatever is still undispositioned
 * and adopts it, timer, draft remark and all.
 */
export function useOpenCall(enabled = true, eager = false) {
  return useQuery({
    queryKey: ['scc-open-call'],
    queryFn: () => api.get('/calls/open').then(unwrap),
    enabled,
    /*
     * Quicker while we are expecting a call rather than merely watching for one.
     *
     * After Ring my phone this poll is the last leg of the wait: the handset has already
     * dialled and the desk is still showing "waiting for your phone" until this notices.
     * Four seconds of that on top of the handset's own delay is most of why the flow felt
     * slow. Once a call is in hand, or when nothing has been asked for, the slower rate
     * is plenty — this runs on every page.
     */
    refetchInterval: eager ? 1200 : 4000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/**
 * The active run — one sitting of calling, as both devices see it.
 *
 * Polled rather than pushed: the phone dials, marks a record done and moves on, and the
 * website has to notice. Five seconds is well inside the length of a call.
 */
export function useActiveRun(enabled = true) {
  return useQuery({
    queryKey: ['scc-active-run'],
    queryFn: () => api.get('/runs/active').then(unwrap),
    enabled,
    refetchInterval: 5000,
    retry: false,
  });
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/runs/start', body).then(unwrap),
    onSuccess: (data) => {
      qc.setQueryData(['scc-active-run'], data);
      qc.invalidateQueries({ queryKey: ['scc-active-run'] });
    },
  });
}

export function useStopRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/runs/stop').then(unwrap),
    onSuccess: () => {
      qc.setQueryData(['scc-active-run'], null);
      qc.invalidateQueries({ queryKey: ['scc-active-run'] });
    },
  });
}

export function useSkipInRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (recordKey) => api.post('/runs/skip', { recordKey }).then(unwrap),
    onSuccess: (data) => qc.setQueryData(['scc-active-run'], data),
  });
}

/**
 * One call's current state, polled while a client is holding it open.
 *
 * Separate from `useOpenCall` on purpose. That one answers "am I on a call" and is what
 * discovers a call dialled elsewhere; this answers "what happened to *this* call", which
 * is the only safe basis for letting go of one. Inferring the answer from the first
 * would throw away a write-up still being typed, because a call stops counting as open
 * once it has been hung up for a while.
 */
export function useCallStatus(callId, enabled = true) {
  return useQuery({
    queryKey: ['scc-call', callId],
    queryFn: () => api.get(`/calls/${callId}`).then(unwrap),
    enabled: enabled && !!callId,
    refetchInterval: 4000,
    retry: false,
  });
}

/** Capture somebody met on the office line, without leaving the write-up. */
export function useAddContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleKey, recordId, ...body }) =>
      api.post(`/work/${moduleKey}/records/${recordId}/contacts`, body).then(unwrap),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scc-record-contacts'] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleKey, recordId, contactId, ...body }) =>
      api.patch(`/work/${moduleKey}/records/${recordId}/contacts/${contactId}`, body).then(unwrap),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scc-record-contacts'] }),
  });
}

// ─── The call itself ──────────────────────────────────────────────
/**
 * Starting a call seeds the open-call cache with the call itself, rather than only
 * invalidating it.
 *
 * Between the POST returning and the next poll landing, `/calls/open` still holds its
 * previous answer — usually null. Anything watching that value would read "no call in
 * progress" for a second and tear down the call that had just been placed. Writing the
 * result straight in closes the window.
 */
export function useBeginCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/calls', body).then(unwrap),
    onSuccess: (created) => {
      qc.setQueryData(['scc-open-call'], created);
      qc.invalidateQueries({ queryKey: ['scc-open-call'] });
    },
  });
}

export function useEndCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, durationSeconds }) =>
      api.post(`/calls/${callId}/end`, { durationSeconds }).then(unwrap),
    // Hung up but not written up: still the open call, now with an end time on it.
    onSuccess: (ended) => qc.setQueryData(['scc-open-call'], ended),
  });
}

/** Live feedback, pushed while the line is still open. */
export function useSaveDraft() {
  return useMutation({
    mutationFn: ({ callId, ...draft }) => api.patch(`/calls/${callId}/draft`, draft).then(unwrap),
  });
}

export function useDispose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, ...body }) => api.post(`/calls/${callId}/disposition`, body).then(unwrap),
    onSuccess: () => {
      // Written up, so there is no open call any more — say so before the next poll.
      qc.setQueryData(['scc-open-call'], null);
      qc.invalidateQueries({ queryKey: ['scc-open-call'] });
      qc.invalidateQueries({ queryKey: ['scc-active-run'] });
      qc.invalidateQueries({ queryKey: ['scc-review-day'] });
      qc.invalidateQueries({ queryKey: ['scc-smart-queue'] });
      qc.invalidateQueries({ queryKey: ['scc-record-notes'] });
    },
  });
}

// ─── End-of-day review ────────────────────────────────────────────
export function useDayReview(date, enabled = true) {
  return useQuery({
    queryKey: ['scc-review-day', date || 'today'],
    queryFn: () => api.get('/reviews/today', { params: date ? { date } : {} }).then(unwrap),
    enabled,
  });
}

export function useReviewHistory(enabled = true) {
  return useQuery({
    queryKey: ['scc-review-history'],
    queryFn: () => api.get('/reviews/history', { params: { limit: 30 } }).then(unwrap),
    enabled,
  });
}

export function useTeamAttendance(enabled = true, days = 7) {
  return useQuery({
    queryKey: ['scc-review-team', days],
    queryFn: () => api.get('/reviews/team', { params: { days } }).then(unwrap),
    enabled,
  });
}

/**
 * One team member's day, for a manager drilling into the attendance grid. Disabled
 * until both a member and a day are chosen, so opening the page costs nothing.
 */
export function useMemberDay(memberId, date) {
  return useQuery({
    queryKey: ['scc-review-member-day', memberId, date],
    queryFn: () => api.get(`/reviews/member/${memberId}`, { params: date ? { date } : {} }).then(unwrap),
    enabled: Boolean(memberId && date),
  });
}

/**
 * One person's activity trail — everything they did, or just one day of it.
 *
 * The same feed the Activity page renders, narrowed to a person a manager has clicked.
 * Authorisation is the server's: it refuses anyone outside the line of report.
 */
export function useMemberActivity(memberId, date) {
  return useQuery({
    queryKey: ['scc-member-activity', memberId, date || 'all'],
    queryFn: () => api.get('/activity', {
      params: { actor: memberId, ...(date ? { date } : {}), limit: 50 },
    }).then((r) => r.data),
    enabled: Boolean(memberId),
  });
}

/** Says what one idle stretch was spent on. */
export function useExplainIdle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, startedAt, reason }) =>
      api.post('/reviews/idle', { date, startedAt, reason }).then(unwrap),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scc-review-day'] }),
  });
}

export function useEditReviewEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, ...body }) => api.patch(`/reviews/entries/${callId}`, body).then(unwrap),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scc-review-day'] }),
  });
}

export function useSubmitDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/reviews/submit', body).then(unwrap),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scc-review-day'] });
      qc.invalidateQueries({ queryKey: ['scc-review-history'] });
      qc.invalidateQueries({ queryKey: ['scc-review-team'] });
      qc.invalidateQueries({ queryKey: ['scc-performance'] });
      qc.invalidateQueries({ queryKey: ['scc-leaderboard'] });
    },
  });
}

export function usePipeline(enabled = true) {
  return useQuery({
    queryKey: ['scc-pipeline'],
    queryFn: () => api.get('/pipeline').then(unwrap),
    enabled,
  });
}

export function useStageDeals(stage, enabled = true) {
  return useQuery({
    queryKey: ['scc-stage-deals', stage],
    queryFn: () => api.get(`/pipeline/${stage}/deals`, { params: { limit: 50 } }).then((r) => r.data),
    enabled: enabled && !!stage,
  });
}

export function usePerformance(params, enabled = true) {
  return useQuery({
    queryKey: ['scc-performance', params],
    queryFn: () => api.get('/performance', { params }).then(unwrap),
    enabled,
  });
}

export function useLeaderboard(params, enabled = true) {
  return useQuery({
    queryKey: ['scc-leaderboard', params],
    queryFn: () => api.get('/performance/leaderboard', { params }).then(unwrap),
    enabled,
  });
}

export function useCustomerSnapshot(company, enabled = true) {
  return useQuery({
    queryKey: ['scc-customer', company],
    queryFn: () => api.get('/customers/snapshot', { params: { company } }).then(unwrap),
    enabled: enabled && !!company,
  });
}

export function useTeamAssignmentList(params, enabled = true) {
  return useQuery({
    queryKey: ['scc-team-assignments-list', params],
    queryFn: () => api.get('/assignments/team', { params }).then((r) => r.data),
    enabled,
  });
}

export function useStrategies(enabled = true) {
  return useQuery({
    queryKey: ['scc-strategies'],
    queryFn: () => api.get('/assignments/strategies').then(unwrap),
    enabled,
    staleTime: Infinity,
  });
}

// ─── Mutations ────────────────────────────────────────────────────

export function useDistributionFacets(moduleKey, enabled = true) {
  return useQuery({
    queryKey: ['scc-dist-facets', moduleKey],
    queryFn: () => api.get(`/assignments/distribute/facets/${moduleKey}`).then(unwrap),
    enabled: enabled && !!moduleKey,
  });
}

export function usePreviewDistribution() {
  return useMutation({
    mutationFn: (body) => api.post('/assignments/distribute/preview', body).then(unwrap),
  });
}

export function useDistribute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/assignments/distribute', body).then(unwrap),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scc-smart-queue'] });
      qc.invalidateQueries({ queryKey: ['scc-module-work'] });
      qc.invalidateQueries({ queryKey: ['scc-team-assignments-list'] });
      qc.invalidateQueries({ queryKey: ['org-activity'] });
    },
  });
}

const invalidateOrg = (qc) => {
  qc.invalidateQueries({ queryKey: ['org-team'] });
  qc.invalidateQueries({ queryKey: ['org-member'] });
  qc.invalidateQueries({ queryKey: ['org-grants'] });
  qc.invalidateQueries({ queryKey: ['org-candidates'] });
  qc.invalidateQueries({ queryKey: ['org-delegation-options'] });
  qc.invalidateQueries({ queryKey: ['org-activity'] });
};

export function useAddMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/team/members', body).then(unwrap),
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, ...body }) => api.patch(`/team/members/${memberId}`, body).then(unwrap),
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useGrantAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, ...body }) => api.post(`/team/members/${memberId}/access`, body).then(unwrap),
    onSuccess: () => invalidateOrg(qc),
  });
}

/**
 * The values a grant can be narrowed to, across every module being granted.
 *
 * Takes one key or several. Granting is a bulk action, so the options have to describe
 * the whole selection — asking per module would hide every country and owner the other
 * modules had. Sorted into the key so two selections of the same modules share a cache
 * entry regardless of the order they were ticked in.
 */
export function useScopeOptions(moduleKeys, enabled = true) {
  const keys = (Array.isArray(moduleKeys) ? moduleKeys : [moduleKeys]).filter(Boolean);
  const sorted = [...keys].sort();
  return useQuery({
    queryKey: ['scc-scope-options', sorted.join(',')],
    queryFn: () => api.get('/team/scope-options', { params: { modules: sorted.join(',') } }).then(unwrap),
    enabled: enabled && sorted.length > 0,
  });
}

export function useGrantAccessBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, ...body }) =>
      api.post(`/team/members/${memberId}/access/bulk`, body).then((r) => r.data.data),
    onSuccess: () => invalidateOrg(qc),
  });
}

export function useRevokeAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, moduleKey }) =>
      api.delete(`/team/members/${memberId}/access/${moduleKey}`).then(unwrap),
    onSuccess: () => invalidateOrg(qc),
  });
}

export default api;

/**
 * Ask this person's own phone to place the call.
 *
 * A browser cannot make a handset dial, and the desktop `tel:` link the Call button used
 * to open does nothing on a PC. So the console leaves an instruction and the phone picks
 * it up on its next poll and runs its own calling flow.
 *
 * Deliberately does NOT create the call here. The phone creates it when it dials, and the
 * live panel adopts it the same way it adopts any call started elsewhere — creating one
 * at both ends would be two records of one conversation.
 */
export function useRequestDial() {
  return useMutation({
    mutationFn: (target) => api.post('/calls/dial-request', {
      module: target.module,
      recordId: target.recordId,
      phone: target.phone,
      company: target.company,
      contactName: target.contactPerson || target.contact?.name,
      contactId: target.contact?.id,
      contactChannel: target.contact?.channel,
    }).then(unwrap),
  });
}

/**
 * Whether the phone has taken the instruction yet: none, waiting, ringing or expired.
 * Polled briefly after asking, so the person clicking knows it was picked up rather than
 * assuming it was.
 */
/**
 * Take back a ring the phone never answered.
 *
 * The same endpoint the handset calls when it finishes with a request — it clears the
 * pending dial for whoever is signed in, and the console is signed in as the same
 * person. Without this a locked phone left every call button disabled until the
 * server's three-minute expiry, with nothing on screen saying why.
 */
export function useCancelDial() {
  return useMutation({
    mutationFn: () => api.post('/calls/dial-request/done').then(unwrap),
  });
}

export function useDialStatus(enabled) {
  return useQuery({
    queryKey: ['scc-dial-status'],
    queryFn: () => api.get('/calls/dial-request').then(unwrap),
    enabled: Boolean(enabled),
    refetchInterval: enabled ? 2000 : false,
  });
}
